import React, { useState, useRef } from 'react';
import { motion } from 'framer-motion';
import emailjs from '@emailjs/browser';

const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.6, delay: i * 0.15, ease: 'easeOut' },
  }),
};

const contactItems = [
  { icon: '📧', label: 'Email', value: 'kagukukibe@gmail.com', href: 'mailto:kagukukibe@gmail.com' },
  { icon: '📱', label: 'Phone', value: '+254 740 027 395', href: 'tel:+254740027395' },
  { icon: '📍', label: 'Location', value: 'Nairobi, Kenya', href: null },
];

const socials = [
  { label: 'GitHub', icon: '🐙', href: 'https://github.com/Thingi01' },
  { label: 'LinkedIn', icon: '💼', href: 'https://linkedin.com/in/' },
  { label: 'WhatsApp', icon: '💬', href: 'https://wa.me/+254740027395' },
];

const Contact = () => {
  const form = useRef();
  const [status, setStatus] = useState('idle');
  const [errorMsg, setErrorMsg] = useState('');

  const sendEmail = (e) => {
    e.preventDefault();
    const email = form.current.user_email.value;
    const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailPattern.test(email)) {
      setErrorMsg('Please enter a valid email address.');
      return;
    }
    setStatus('loading');
    setErrorMsg('');
    emailjs.sendForm('service_hnkq32a', 'template_8lrh67a', form.current, 'D9WlAv-mpOSfqre2A')
      .then(() => {
        setStatus('success');
        form.current.reset();
      })
      .catch(() => {
        setStatus('error');
      });
  };

  return (
    <div className="relative py-32 px-6 bg-[#0d1427] overflow-hidden">
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/40 to-transparent" />
      <div className="absolute top-20 left-0 w-72 h-72 bg-cyan-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-20 right-0 w-72 h-72 bg-blue-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="max-w-6xl mx-auto">

        <motion.div
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="flex items-center gap-4 mb-6"
        >
          <span className="font-mono text-cyan-400 text-sm">05.</span>
          <h2 className="text-3xl md:text-4xl font-black text-white">Contact</h2>
          <div className="flex-1 h-px bg-gradient-to-r from-cyan-500/40 to-transparent" />
        </motion.div>

        <motion.p
          variants={fadeUp}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="text-slate-400 mb-16 max-w-xl"
        >
          Have a project in mind or want to collaborate? I would love to hear from you.
        </motion.p>

        <div className="flex flex-col lg:flex-row gap-12">

          <div className="w-full lg:w-2/5 space-y-6">
            <motion.div
              variants={fadeUp}
              custom={0}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
            >
              <h3 className="text-white font-bold text-xl mb-4">
                Let us build something great together
              </h3>
              <p className="text-slate-400 leading-relaxed text-sm">
                I am always open to exciting collaborations, freelance opportunities,
                and new challenges. Whether you need a website, a design, or data insights, let us talk.
              </p>
            </motion.div>

            <div className="space-y-3">
              {contactItems.map(({ icon, label, value, href }, i) => (
                <motion.div
                  key={label}
                  variants={fadeUp}
                  custom={i + 1}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  className="flex items-center gap-4 p-4 rounded-xl bg-white/[0.02] border border-white/[0.06] hover:border-cyan-500/20 transition-all duration-300"
                >
                  <div className="w-10 h-10 rounded-lg bg-cyan-500/10 border border-cyan-500/20 flex items-center justify-center text-lg flex-shrink-0">
                    {icon}
                  </div>
                  <div>
                    <div className="text-slate-500 text-xs uppercase tracking-widest mb-0.5">{label}</div>
                    {href ? (
                      <a href={href} className="text-slate-200 text-sm font-medium hover:text-cyan-400 transition-colors">
                        {value}
                      </a>
                    ) : (
                      <span className="text-slate-200 text-sm font-medium">{value}</span>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>

            <motion.div
              variants={fadeUp}
              custom={4}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true }}
              className="flex flex-wrap gap-3 pt-2"
            >
              {socials.map(({ label, icon, href }) => (
                
                 <a key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-white/[0.03] border border-white/[0.06] hover:border-cyan-500/30 hover:bg-cyan-500/5 text-slate-400 hover:text-cyan-400 transition-all duration-300 text-sm"
                >
                  <span>{icon}</span>
                  <span>{label}</span>
                </a>
              ))}
            </motion.div>
          </div>

          <motion.div
            variants={fadeUp}
            custom={1}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            className="w-full lg:w-3/5"
          >
            <form ref={form} onSubmit={sendEmail} className="space-y-4">

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-slate-400 text-xs uppercase tracking-widest mb-2">Name</label>
                  <input
                    type="text"
                    name="user_name"
                    required
                    placeholder="Your full name"
                    disabled={status === 'loading'}
                    className="w-full bg-white/[0.03] border border-white/[0.08] focus:border-cyan-500/50 rounded-xl px-4 py-3 text-slate-200 text-sm placeholder-slate-600 outline-none transition-all duration-300"
                  />
                </div>
                <div>
                  <label className="block text-slate-400 text-xs uppercase tracking-widest mb-2">Email</label>
                  <input
                    type="email"
                    name="user_email"
                    required
                    placeholder="your@email.com"
                    disabled={status === 'loading'}
                    className="w-full bg-white/[0.03] border border-white/[0.08] focus:border-cyan-500/50 rounded-xl px-4 py-3 text-slate-200 text-sm placeholder-slate-600 outline-none transition-all duration-300"
                  />
                </div>
              </div>

              <div>
                <label className="block text-slate-400 text-xs uppercase tracking-widest mb-2">Subject</label>
                <input
                  type="text"
                  name="subject"
                  placeholder="What is this about?"
                  disabled={status === 'loading'}
                  className="w-full bg-white/[0.03] border border-white/[0.08] focus:border-cyan-500/50 rounded-xl px-4 py-3 text-slate-200 text-sm placeholder-slate-600 outline-none transition-all duration-300"
                />
              </div>

              <div>
                <label className="block text-slate-400 text-xs uppercase tracking-widests mb-2">Message</label>
                <textarea
                  name="message"
                  rows={5}
                  required
                  placeholder="Tell me about your project..."
                  disabled={status === 'loading'}
                  className="w-full bg-white/[0.03] border border-white/[0.08] focus:border-cyan-500/50 rounded-xl px-4 py-3 text-slate-200 text-sm placeholder-slate-600 outline-none transition-all duration-300 resize-none"
                />
              </div>

              {errorMsg && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  <span>x</span> {errorMsg}
                </div>
              )}

              {status === 'success' && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-green-500/10 border border-green-500/20 text-green-400 text-sm">
                  <span>✓</span> Message sent! I will get back to you soon.
                </div>
              )}

              {status === 'error' && (
                <div className="flex items-center gap-2 p-3 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
                  <span>x</span> Failed to send. Please try again or email me directly.
                </div>
              )}

              <button
                type="submit"
                disabled={status === 'loading'}
                className="w-full py-4 rounded-xl bg-cyan-500 hover:bg-cyan-400 disabled:opacity-50 text-[#0a0f1e] font-bold text-sm uppercase tracking-widest transition-all duration-300 hover:scale-[1.02] hover:shadow-lg hover:shadow-cyan-500/30"
              >
                {status === 'loading' ? 'Sending...' : 'Send Message'}
              </button>

            </form>
          </motion.div>

        </div>
      </div>

      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-cyan-500/20 to-transparent" />
    </div>
  );
};

export default Contact;